var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { reDraw } from '@/helpers/common';
import { usePrevious } from '@/hooks/lifecycle/usePrevious';
import { useBoolState } from '@/hooks/state/useBoolState';
import { sleep } from '@/utils/async/sleep';
// アコーディオン開閉時間
export const transitionTime = 250;
/**
 * 匿名コンサルホットラインチャットメッセージ詳細カスタムフック
 */
export const useHotlineClientNegotiateContent = (options) => {
    const { childrenWrapRef, heightRef, isSelected, onClick } = options;
    const { boolState: isOpen, changeFalse, changeTrue, } = useBoolState({
        initialState: isSelected,
    });
    const previewIsChecked = usePrevious(isSelected);
    // 表示状態の変遷の指定
    const viewMode = useMemo(() => {
        if (previewIsChecked === false && isSelected) {
            // false => trueになった場合
            return 'update_on';
        }
        if (previewIsChecked === true && !isSelected) {
            // true => falseになった場合
            return 'update_off';
        }
        if (previewIsChecked === undefined && isSelected) {
            // 初期表示の場合
            return 'default_on';
        }
        // 初期表示ではない場合
        return 'default_off';
    }, [previewIsChecked, isSelected]);
    // 要素の高さの指定
    const [height, changeHeight] = useState('0px');
    // 完全に開いているかどうか
    const [isFullOpen, setIsFullOpen] = useState(false);
    heightRef.current = height;
    const collapse = useCallback((startHeight, doingHeight, endHeight) => __awaiter(void 0, void 0, void 0, function* () {
        setIsFullOpen(false);
        // 既に最終状態、もしくはマウントされたコンポーネント自体が存在しない場合は何もしない
        if (heightRef.current === endHeight || !childrenWrapRef.current) {
            return;
        }
        // 初期設定
        changeHeight(startHeight);
        yield sleep(0);
        if (!childrenWrapRef.current) {
            return;
        }
        reDraw(childrenWrapRef.current);
        yield sleep(0);
        if (!childrenWrapRef.current) {
            return;
        }
        // 初期設定から移動先の高さへ変更
        changeHeight(doingHeight);
        yield sleep(0);
        if (!childrenWrapRef.current) {
            return;
        }
        reDraw(childrenWrapRef.current);
        yield sleep(transitionTime);
        if (!childrenWrapRef.current) {
            return;
        }
        // 最終設定の高さに
        changeHeight(endHeight);
        yield sleep(0);
        if (!childrenWrapRef.current) {
            return;
        }
        reDraw(childrenWrapRef.current);
        yield sleep(0);
    }), [childrenWrapRef, heightRef, changeHeight]);
    const collapseOn = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const currentHeight = (_a = childrenWrapRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
        yield collapse(`0px`, `${currentHeight}px`, 'auto');
        // 開き終わったら state 更新
        if (childrenWrapRef.current) {
            setIsFullOpen(true);
        }
    }), [childrenWrapRef, collapse]);
    const collapseOff = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        const currentHeight = (_b = childrenWrapRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height;
        yield collapse(`${currentHeight}px`, `0px`, `0px`);
    }), [childrenWrapRef, collapse]);
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (viewMode === 'default_on') {
                yield collapseOn();
            }
            if (viewMode === 'update_on') {
                // false => trueになった場合
                changeHeight('0px');
                changeTrue();
                yield sleep(0);
                yield collapseOn();
            }
            if (viewMode === 'update_off') {
                // true => falseになった場合
                yield collapseOff();
                changeFalse();
            }
        }))();
    }, [
        viewMode,
        childrenWrapRef,
        changeTrue,
        changeFalse,
        collapseOn,
        collapseOff,
    ]);
    /**
     * 項目クリック時の処理
     */
    const onClickListItem = useCallback((value) => __awaiter(void 0, void 0, void 0, function* () {
        onClick(value);
        yield collapseOn();
    }), [onClick, collapseOn]);
    return {
        collapseOff,
        collapseOn,
        height,
        isFullOpen,
        isOpen,
        onClickListItem,
    };
};
